export const SCHEDULED_MAINTENANCE_CODE = 'SCHEDULED_MAINTENANCE_ACTIVE';

const SAFE_MESSAGE_PREFIX = 'Brief scheduled maintenance — back by ';
const SAFE_MESSAGE_SUFFIX = '. Your tickets and orders are safe.';
const SAFE_MESSAGE_PATTERN =
  /^Brief scheduled maintenance — back by \d{2}:\d{2} (?:BST|GMT)\. Your tickets and orders are safe\.$/;

let currentNotice = null;
const listeners = new Set();

const getPayload = (value) => {
  if (value?.response?.data) return value.response.data;
  if (value?.data) return value.data;
  return value;
};

const buildSafeMessageFromEndsAt = (value) => {
  if (typeof value !== 'string') return '';
  const endsAt = new Date(value);
  if (!Number.isFinite(endsAt.getTime()) || endsAt.toISOString() !== value) return '';
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short'
  });
  return `${SAFE_MESSAGE_PREFIX}${formatter.format(endsAt).replace(',', '')}${SAFE_MESSAGE_SUFFIX}`;
};

const getSafeMessage = (maintenance) => {
  const message = String(maintenance?.message || '').trim();
  if (SAFE_MESSAGE_PATTERN.test(message)) {
    return message;
  }
  return buildSafeMessageFromEndsAt(maintenance?.endsAt);
};

export const getMaintenanceNotice = (value) => {
  const payload = getPayload(value);
  const maintenance = payload?.maintenance;
  const active = maintenance?.active === true
    || payload?.code === SCHEDULED_MAINTENANCE_CODE;

  if (!active) return null;
  const message = getSafeMessage(maintenance || payload);
  // Active barrier boots cannot exist without a canonical future endsAt, and
  // the server always emits the exact ruled sentence. Reject a malformed
  // envelope instead of inventing a second buyer-facing maintenance message.
  if (!message) return null;

  return {
    active: true,
    code: SCHEDULED_MAINTENANCE_CODE,
    endsAt: maintenance?.endsAt || null,
    message
  };
};

export const publishMaintenanceNotice = (value) => {
  const notice = getMaintenanceNotice(value);
  if (!notice) return null;

  currentNotice = notice;
  listeners.forEach((listener) => listener(currentNotice));
  return notice;
};

export const clearMaintenanceNotice = () => {
  if (!currentNotice) return false;

  currentNotice = null;
  listeners.forEach((listener) => listener(currentNotice));
  return true;
};

export const subscribeToMaintenance = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getCurrentMaintenanceNotice = () => currentNotice;

export const isMaintenanceError = (error) => Boolean(getMaintenanceNotice(error));

export const getMaintenanceMessage = (value) => (
  getMaintenanceNotice(value)?.message || ''
);

// Test-only reset. Keeping it out of the UI avoids any OFF-state polling or requests.
export const resetMaintenanceNoticeForTests = () => {
  clearMaintenanceNotice();
};
